import { useState, useEffect } from 'react'
import { listasApi } from '../api/listaPrecios'
import Boton from '../components/Boton'

export default function ListasPreciosPage() {
  const [listas, setListas] = useState([])
  const [listaActiva, setListaActiva] = useState(null)
  const [items, setItems] = useState([])
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [meta, setMeta] = useState({ last_page: 1, total: 0 })
  const [loadingListas, setLoadingListas] = useState(true)
  const [loadingItems, setLoadingItems] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    listasApi.listar()
      .then(({ data }) => setListas(data.data || data))
      .catch(err => {
        if (err.response?.status === 404) {
          setError('Endpoint /listas-precios no disponible. Agregar rutas al backend para habilitar esta pantalla.')
        } else {
          setError('Error al cargar listas de precios.')
        }
      })
      .finally(() => setLoadingListas(false))
  }, [])

  // Items paginados server-side (la lista entera no entra en memoria: 14k+ artículos).
  // El buscador también va al server, con debounce.
  useEffect(() => {
    if (!listaActiva) return
    const timer = setTimeout(async () => {
      setLoadingItems(true)
      try {
        const { data } = await listasApi.items(listaActiva.id, { page, q: query || undefined })
        setItems(data.data || [])
        setMeta({ last_page: data.last_page || 1, total: data.total || 0 })
      } catch { setItems([]) } finally { setLoadingItems(false) }
    }, query ? 350 : 0)
    return () => clearTimeout(timer)
  }, [listaActiva, page, query])

  const cargarItems = (lista) => {
    setListaActiva(lista)
    setItems([])
    setQuery('')
    setPage(1)
  }

  const buscar = (q) => { setQuery(q); setPage(1) }

  const itemsFiltrados = items

  return (
    <div style={st.wrap}>
      <h2 style={st.title}>Listas de Precios</h2>

      {error && <div style={st.errorBox}>{error}</div>}

      <div style={st.layout}>
        <div style={st.listPanel}>
          <h3 style={st.panelTitle}>Listas disponibles</h3>
          {loadingListas && <p style={st.hint}>Cargando...</p>}
          {!loadingListas && listas.length === 0 && !error && (
            <p style={st.hint}>Sin listas registradas.</p>
          )}
          {listas.map(l => (
            <ListaItem key={l.id} lista={l} activa={listaActiva?.id === l.id} onSelect={cargarItems} onTelefonoActualizado={(nuevo) => setListas(prev => prev.map(x => x.id === l.id ? { ...x, telefono: nuevo } : x))} />
          ))}
        </div>

        <div style={st.itemPanel}>
          {!listaActiva ? (
            <p style={st.hint}>Seleccioná una lista para ver sus precios.</p>
          ) : (
            <>
              <div style={st.itemHeader}>
                <h3 style={st.panelTitle}>
                  {listaActiva.nombre}
                  {listaActiva.version ? <span style={{ ...st.listaVersion, marginLeft: 8 }}>· v{listaActiva.version}</span> : null}
                </h3>
                <input
                  style={st.search}
                  placeholder="Buscar por código o descripción..."
                  value={query}
                  onChange={e => buscar(e.target.value)}
                />
              </div>
              {loadingItems ? (
                <p style={st.hint}>Cargando artículos...</p>
              ) : itemsFiltrados.length === 0 ? (
                <p style={st.hint}>{query ? `Sin resultados para "${query}".` : 'Sin artículos en esta lista.'}</p>
              ) : (
                <table style={st.table}>
                  <thead>
                    <tr>
                      <th style={st.th}>Código</th>
                      <th style={st.th}>Descripción</th>
                      <th style={{ ...st.th, textAlign: 'right' }}>P. lista</th>
                      <th style={{ ...st.th, textAlign: 'right' }}>Dto 1</th>
                      <th style={{ ...st.th, textAlign: 'right' }}>Dto 2</th>
                      <th style={{ ...st.th, textAlign: 'right' }}>Precio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsFiltrados.map(it => {
                      // Precio de SOLO LECTURA (Martin, 31 ago 2026): por ahora el
                      // precio no se edita desde LGR — lo trae el sync de Presea.
                      return (
                        <tr key={it.articulo_id} style={st.tr}>
                          <td style={st.td}><code>{it.articulo?.codigo_alfa}</code></td>
                          <td style={st.td}>{it.articulo?.descripcion}</td>
                          <td style={st.tdNum}>
                            {it.pr_lista != null ? `$${Number(it.pr_lista).toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : '—'}
                          </td>
                          <td style={st.tdNum}>{Number(it.dto_1) > 0 ? `${Number(it.dto_1)}%` : '—'}</td>
                          <td style={st.tdNum}>{Number(it.dto_2) > 0 ? `${Number(it.dto_2)}%` : '—'}</td>
                          <td style={{ ...st.tdNum, color: undefined, fontWeight: 600 }}>
                            ${Number(it.precio).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
              {meta.total > 0 && (
                <div style={st.pager}>
                  <span style={st.pagerInfo}>
                    {meta.total} artículo{meta.total !== 1 ? 's' : ''} · página {page} de {meta.last_page}
                  </span>
                  <Boton variante="ghost" sm disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Anterior</Boton>
                  <Boton variante="ghost" sm disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)}>Siguiente →</Boton>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Item de la lista lateral (panel izquierdo). Muestra nombre + código + teléfono
 * / WhatsApp del canal. El teléfono se edita inline con un ✎ (no abre modal,
 * mínima fricción). Va al footer del mail al cliente (ModalCompartirCliente).
 */
function ListaItem({ lista, activa, onSelect, onTelefonoActualizado }) {
  const [editando, setEditando] = useState(false)
  const [valor, setValor] = useState(lista.telefono || '')
  const [guardando, setGuardando] = useState(false)

  const guardar = async () => {
    setGuardando(true)
    try {
      const { data } = await listasApi.actualizar(lista.id, { telefono: valor.trim() || null })
      onTelefonoActualizado(data.telefono ?? null)
      setEditando(false)
    } catch (e) {
      window.alert(e.response?.data?.message || 'No se pudo guardar el teléfono.')
    } finally { setGuardando(false) }
  }

  return (
    <div
      style={{ ...st.listaItem, ...(activa ? st.listaItemActive : {}) }}
      onClick={() => !editando && onSelect(lista)}
    >
      <strong style={{ fontSize: '0.875rem' }}>{lista.nombre}</strong>
      <span style={st.listaCod}>
        {lista.codigo_presea}
        {lista.version ? <span style={st.listaVersion}> · v{lista.version}</span> : null}
      </span>
      {editando ? (
        <div style={{ display: 'flex', gap: 4, marginTop: 4 }} onClick={e => e.stopPropagation()}>
          <input
            style={st.telInput}
            value={valor}
            onChange={e => setValor(e.target.value)}
            placeholder="+5491170546060"
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter') guardar(); if (e.key === 'Escape') { setEditando(false); setValor(lista.telefono || '') } }}
          />
          <Boton variante="primary" sm disabled={guardando} onClick={guardar}>{guardando ? '…' : '✓'}</Boton>
          <Boton variante="ghost" sm onClick={() => { setEditando(false); setValor(lista.telefono || '') }}>✕</Boton>
        </div>
      ) : (
        <div style={st.telRow} onClick={e => { e.stopPropagation(); setEditando(true) }}>
          {lista.telefono
            ? <span style={st.telTexto}>📱 {lista.telefono}</span>
            : <span style={st.telVacio}>📱 Sin teléfono — clic para cargar</span>}
          <span style={st.telEdit}>✎</span>
        </div>
      )}
    </div>
  )
}

const st = {
  wrap: { maxWidth: '1100px' },
  title: { margin: '0 0 1.25rem', fontSize: '1.25rem', color: '#1e293b' },
  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.875rem' },
  layout: { display: 'grid', gridTemplateColumns: '240px 1fr', gap: '1rem', alignItems: 'start' },
  listPanel: { background: 'white', borderRadius: '8px', padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  itemPanel: { background: 'white', borderRadius: '8px', padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', minHeight: '200px' },
  panelTitle: { margin: '0 0 0.75rem', fontSize: '0.9375rem', fontWeight: '700', color: '#1e293b' },
  hint: { color: '#94a3b8', fontSize: '0.875rem' },
  listaItem: { padding: '0.625rem 0.75rem', borderRadius: '6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '2px', border: '1px solid transparent' },
  listaItemActive: { background: '#eff6ff', borderColor: '#bfdbfe' },
  listaCod: { fontSize: '0.75rem', color: '#64748b' },
  listaVersion: { fontSize: '0.75rem', color: '#94a3b8', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
  telRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, fontSize: '0.75rem', marginTop: 4, padding: '2px 0', cursor: 'pointer' },
  telTexto: { color: '#0f172a', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
  telVacio: { color: '#94a3b8', fontStyle: 'italic' },
  telEdit: { color: '#94a3b8', fontSize: '0.7rem' },
  telInput: { padding: '0.25rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.75rem', flex: 1, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', boxSizing: 'border-box' },
  itemHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', gap: '1rem', flexWrap: 'wrap' },
  search: { padding: '0.4rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', width: '280px', boxSizing: 'border-box' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th: { padding: '0.5rem 0.75rem', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '700', fontSize: '0.8125rem', color: '#64748b' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '0.4rem 0.75rem', verticalAlign: 'middle' },
  tdNum: { padding: '0.4rem 0.75rem', verticalAlign: 'middle', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: '#6b7280' },
  pager: { display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem' },
  pagerInfo: { fontSize: '0.8125rem', color: '#64748b', marginRight: 'auto' },
}
